import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { Autocomplete, Box, Chip, TextField } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TOUCH_TARGET_PX } from '../../../../config/touch-target';
import { useConsultantDeadlineSummary } from '../../../../hooks/consultant/use-consultant-deadlines';
import { useDebouncedValue } from '../../../../hooks/use-debounced-value';

const SEARCH_DEBOUNCE_MS = 300;

/**
 * La X della chip resta un'icona da 20 px, ma l'area che si tocca è 48×48: i margini negativi la
 * tengono dentro l'altezza della chip, sporgendo solo nei bordi vuoti.
 */
const CLIENT_CHIP_SX = {
  '& .MuiChip-deleteIcon': {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: TOUCH_TARGET_PX,
    height: TOUCH_TARGET_PX,
    m: '-8px -8px -8px -14px',
    '& svg': { fontSize: 20 },
  },
} as const;

export interface ClientOption {
  tenantId: string;
  tenantName: string;
  /** Dal server: l'azienda usa Prospero e qualcuno firma dall'app. Assente = non noto (bozza dell'assistente). */
  canRequestCompanySignature?: boolean;
}

interface ConsultantClientMultiSelectProps {
  selected: ClientOption[];
  onChange: (next: ClientOption[]) => void;
  /** One client at a time: `selected` and `onChange` still carry a list (zero or one client). */
  single?: boolean;
  error?: boolean;
  helperText?: string;
}

/** Clienti del perimetro, cercati sul server (stessa fonte dello scadenzario): mai una lista completa in memoria. */
export function ConsultantClientMultiSelect({
  selected,
  onChange,
  single = false,
  error,
  helperText,
}: ConsultantClientMultiSelectProps) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const search = useDebouncedValue(inputValue, SEARCH_DEBOUNCE_MS);
  const { tenantSummaries, isLoading } = useConsultantDeadlineSummary({
    page: 1,
    search,
    tenantId: '',
  });
  const clientOptions = useMemo<ClientOption[]>(() => {
    const foundClients = tenantSummaries.map((summary) => ({
      tenantId: summary.tenantId,
      tenantName: summary.tenantName,
      canRequestCompanySignature: summary.canRequestCompanySignature,
    }));
    const foundIds = new Set(foundClients.map((option) => option.tenantId));
    return [...selected.filter((option) => !foundIds.has(option.tenantId)), ...foundClients];
  }, [tenantSummaries, selected]);

  const sharedProps = {
    options: clientOptions,
    loading: isLoading,
    filterOptions: (candidates: ClientOption[]) => candidates,
    getOptionLabel: (option: ClientOption) => option.tenantName,
    isOptionEqualToValue: (option: ClientOption, value: ClientOption) =>
      option.tenantId === value.tenantId,
    onInputChange: (_event: unknown, next: string, reason: string) => {
      if (reason === 'input') {
        setInputValue(next);
      }
    },
    noOptionsText: t('consultant.deadlines.create.clientsNoOptions'),
    loadingText: t('consultant.deadlines.create.clientsLoading'),
  };

  const helper =
    helperText ??
    (single
      ? t('consultant.deadlines.create.clientHelperSingle', {
          defaultValue: 'Il cliente a cui appartiene il documento',
        })
      : t('consultant.deadlines.create.clientsHelper'));
  const label = single
    ? t('consultant.deadlines.create.clientLabel', { defaultValue: 'Cliente' })
    : t('consultant.deadlines.create.clientsLabel');

  function renderInput(params: Parameters<typeof TextField>[0]) {
    return (
      <TextField
        {...params}
        label={label}
        placeholder={t('consultant.deadlines.create.clientsPlaceholder')}
        error={error}
        helperText={helper}
        required
        slotProps={{
          ...params.slotProps,
          htmlInput: {
            ...(params.slotProps?.htmlInput as object),
            'data-testid': 'consultant-deadline-create-clients',
          },
        }}
      />
    );
  }

  if (single) {
    return (
      <Autocomplete
        {...sharedProps}
        value={selected[0] ?? null}
        onChange={(_event, next) => onChange(next ? [next] : [])}
        renderInput={renderInput}
      />
    );
  }

  return (
    <Autocomplete
      {...sharedProps}
      multiple
      disableCloseOnSelect
      value={selected}
      onChange={(_event, next) => onChange(next)}
      renderValue={(value, getItemProps) =>
        value.map((option, index) => {
          const { key, ...itemProps } = getItemProps({ index });
          return (
            <Chip
              key={key}
              label={option.tenantName}
              {...itemProps}
              sx={CLIENT_CHIP_SX}
              deleteIcon={
                <Box
                  component="span"
                  data-testid={`consultant-client-chip-delete-${option.tenantId}`}
                >
                  <CancelOutlinedIcon aria-hidden />
                </Box>
              }
            />
          );
        })
      }
      renderInput={renderInput}
    />
  );
}
